import { useState, useEffect, useRef, useCallback } from "react";
import { trackEvent } from "@/lib/analytics";

/**
 * Quick-filter picker for search inputs.
 *
 * Self-attaching component — give it the input ref + a `setValue`
 * callback and it wires up its own keydown, change, and select
 * listeners on the input. Renders a dropdown menu positioned beneath
 * the input when the user types `:` at the start of a word.
 *
 * Pattern: hook + view, not parent-coordinated. Both `/search` and the
 * Command Palette mount this component identically; differences in
 * input library (native input vs cmdk Command.Input) are handled by
 * listening to native DOM events on the underlying element rather than
 * React-controlled prop callbacks.
 */
const PICKER_ITEMS = [
  { id: "is:done",   insert: "is:done ",   label: "is:done",    desc: "Completed tasks" },
  { id: "is:open",   insert: "is:open ",   label: "is:open",    desc: "Open tasks" },
  { id: "project:",  insert: "project:",   label: "project:",   desc: "Filter by project name" },
  { id: "priority:", insert: "priority:",  label: "priority:",  desc: "Filter by priority (high, medium, low)" },
  { id: "tag:",      insert: "tag:",       label: "tag:",       desc: "Filter by tag" },
  { id: "before:",   insert: "before:",    label: "before:",    desc: "Due/created before date" },
  { id: "after:",    insert: "after:",     label: "after:",     desc: "Due/created after date" },
];

/**
 * Detect the trigger word at the cursor. Returns the index of the
 * leading `:` if the current word starts with `:` and contains no
 * whitespace, otherwise -1.
 */
function detectTrigger(value, cursor) {
  let wordStart = cursor;
  while (wordStart > 0 && !/\s/.test(value[wordStart - 1])) wordStart--;
  const word = value.slice(wordStart, cursor);
  return word.startsWith(":") ? wordStart : -1;
}

/**
 * @param {object} props
 * @param {React.RefObject<HTMLInputElement>} props.inputRef
 * @param {string} props.value
 * @param {(next: string, cursor: number) => void} props.setValue
 *   Called on operator-insert with the new query and the desired cursor
 *   position. Caller is responsible for actually updating React state
 *   AND restoring the cursor on the input.
 * @param {string} [props.zClass] tailwind z-index class for the dropdown.
 *   Defaults to z-30 — palette consumers should pass `z-[1100]` so the
 *   dropdown sits above the palette card.
 */
export default function SearchOperatorPicker({ inputRef, value, setValue, zClass = "z-30" }) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(-1);
  const [picked, setPicked] = useState(0);

  // Track the latest value via ref so listeners attached once at mount
  // don't capture stale closures.
  const valueRef = useRef(value);
  useEffect(() => { valueRef.current = value; }, [value]);

  const close = useCallback(() => {
    setOpen(false);
    setStart(-1);
    setPicked(0);
  }, []);

  const insertItem = useCallback((item) => {
    const node = inputRef.current;
    if (!node || start < 0) return;
    const cursor = node.selectionStart ?? valueRef.current.length;
    const before = valueRef.current.slice(0, start);
    const after = valueRef.current.slice(cursor);
    const sep = before && !before.endsWith(" ") ? " " : "";
    const inserted = `${before}${sep}${item.insert}${after}`;
    const newCursor = before.length + sep.length + item.insert.length;
    setValue(inserted, newCursor);
    close();
    requestAnimationFrame(() => {
      const n = inputRef.current;
      if (n) {
        n.focus();
        try { n.setSelectionRange(newCursor, newCursor); } catch { /* input type may not support selection ranges — cursor placement is cosmetic */ }
      }
    });
  }, [inputRef, start, setValue, close]);

  // Recompute trigger whenever the controlled value changes (handles
  // both typing and external setValue calls).
  useEffect(() => {
    const node = inputRef.current;
    if (!node) return;
    if (document.activeElement !== node) return;
    const cursor = node.selectionStart ?? value.length;
    const triggerStart = detectTrigger(value, cursor);
    if (triggerStart >= 0) {
      // One-shot conversion analytics: did the `:` hint pulse actually
      // teach the user the picker? Fire once, the first time a `:`
      // trigger lands AFTER the hint had been displayed.
      if (!open) {
        try {
          if (
            localStorage.getItem("mmntm:operator_hint_shown") === "1" &&
            localStorage.getItem("mmntm:operator_hint_converted") !== "1"
          ) {
            localStorage.setItem("mmntm:operator_hint_converted", "1");
            trackEvent("operator_hint_converted", {});
          }
        } catch { /* localStorage unavailable — hint conversion is best-effort */ }
      }
      setOpen(true);
      setStart(triggerStart);
      setPicked(0);
    } else if (open) {
      close();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Filtered list — anchored to current cursor, recomputed every render
  // so it stays accurate as the user types AFTER the trigger.
  const cursor = inputRef.current?.selectionStart ?? value.length;
  const filterText = open && start >= 0
    ? value.slice(start + 1, Math.max(start + 1, cursor)).toLowerCase()
    : "";
  const filtered = open
    ? PICKER_ITEMS.filter((it) => !filterText || it.label.toLowerCase().includes(filterText))
    : [];

  // Keyboard handling — attach a native listener on the input so we
  // run BEFORE React's synthetic event handlers (capture phase, native
  // listener fires first because it's added directly to the DOM node).
  useEffect(() => {
    const node = inputRef.current;
    if (!node) return;

    const onKeyDown = (e) => {
      if (!open) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setPicked((i) => (i + 1) % Math.max(1, filtered.length));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setPicked((i) => (i - 1 + Math.max(1, filtered.length)) % Math.max(1, filtered.length));
      } else if (e.key === "Enter") {
        if (filtered.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          insertItem(filtered[Math.min(picked, filtered.length - 1)]);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        close();
      }
    };

    // capture=true so we run before any React/cmdk handlers attached
    // higher up the tree.
    node.addEventListener("keydown", onKeyDown, true);
    return () => node.removeEventListener("keydown", onKeyDown, true);
  }, [inputRef, open, filtered, picked, insertItem, close]);

  // Track cursor moves (arrow keys without typing, mouse clicks inside
  // input) — close the picker if the cursor leaves the trigger word.
  useEffect(() => {
    const node = inputRef.current;
    if (!node) return;
    const onSelect = () => {
      if (!open) return;
      const c = node.selectionStart ?? 0;
      if (start < 0 || c <= start) { close(); return; }
      const span = valueRef.current.slice(start, c);
      if (!span.startsWith(":") || /\s/.test(span)) close();
    };
    node.addEventListener("select", onSelect);
    node.addEventListener("click", onSelect);
    node.addEventListener("keyup", onSelect);
    return () => {
      node.removeEventListener("select", onSelect);
      node.removeEventListener("click", onSelect);
      node.removeEventListener("keyup", onSelect);
    };
  }, [inputRef, open, start, close]);

  // Close on blur (with a short delay so a mousedown on the dropdown
  // can complete its insert before close fires).
  useEffect(() => {
    const node = inputRef.current;
    if (!node) return;
    const onBlur = () => { setTimeout(() => close(), 180); };
    node.addEventListener("blur", onBlur);
    return () => node.removeEventListener("blur", onBlur);
  }, [inputRef, close]);

  if (!open || filtered.length === 0) return null;

  return (
    <div
      className={`absolute left-0 right-0 mt-1 rounded-lg border shadow-lg overflow-hidden ${zClass}`}
      style={{
        top: "100%",
        background: "var(--tm-bg-card)",
        borderColor: "var(--tm-border-color)",
      }}
      role="listbox"
      data-testid="quick-filter-picker"
    >
      <div
        className="px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider"
        style={{
          background: "var(--tm-bg-secondary)",
          color: "var(--tm-text-muted)",
          borderBottom: "1px solid var(--tm-border-color)",
        }}
      >
        Filter operators
      </div>
      {filtered.map((item, idx) => {
        const isPicked = idx === Math.min(picked, filtered.length - 1);
        return (
          <button
            key={item.id}
            type="button"
            role="option"
            aria-selected={isPicked}
            onMouseEnter={() => setPicked(idx)}
            onMouseDown={(e) => { e.preventDefault(); insertItem(item); }}
            className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left transition-colors"
            style={{
              background: isPicked ? "color-mix(in srgb, var(--tm-accent) 12%, transparent)" : "transparent",
              color: "var(--tm-text-primary)",
            }}
            data-testid={`picker-item-${item.id}`}
          >
            <code
              className="font-mono text-[11px] px-1.5 py-0.5 rounded"
              style={{
                background: "color-mix(in srgb, var(--tm-accent) 12%, transparent)",
                color: "var(--tm-accent)",
              }}
            >
              {item.label}
            </code>
            <span className="text-[11px] truncate" style={{ color: "var(--tm-text-muted)" }}>
              {item.desc}
            </span>
          </button>
        );
      })}
      <div
        className="px-3 py-1.5 text-[9px] flex items-center gap-3"
        style={{
          background: "var(--tm-bg-secondary)",
          color: "var(--tm-text-muted)",
          borderTop: "1px solid var(--tm-border-color)",
        }}
      >
        <span><kbd className="font-mono">↑↓</kbd> nav</span>
        <span><kbd className="font-mono">↵</kbd> select</span>
        <span><kbd className="font-mono">esc</kbd> close</span>
      </div>
    </div>
  );
}
